import { Popconfirm, Tooltip } from 'antd';
import {
  ContainerOutlined,
  EditOutlined,
  EyeOutlined,
  RollbackOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

/**
 * Creates an action column configuration for data tables.
 * This utility function eliminates code duplication by generating the common structure
 * used across multiple column files (owners, pensioners, etc.).
 *
 * @param {Object} params - Configuration parameters
 * @param {Function} params.t - Translation function from useTranslation hook
 * @param {Function} params.deleteResource - Function to delete/archive a resource
 * @param {Function} params.restoreResource - Function to restore/unarchive a resource
 * @param {string} params.resourcePath - Base path for the resource (e.g., 'owners', 'pensioners')
 * @returns {Object} Column configuration object for actions
 */
export const createActionColumn = ({
  t,
  deleteResource,
  restoreResource,
  resourcePath
}) => ({
  key: 'actions',
  align: 'right',
  fixed: 'right',
  width: 100,
  render: (record) => (
    <div className="table-actions">
      <Tooltip title={t('datatable.column.action.show.hover')} placement="left">
        <Link to={{ pathname: `/${resourcePath}/show/${record._id}` }}>
          <EyeOutlined />
        </Link>
      </Tooltip>
      <Tooltip title={t('datatable.column.action.edit.hover')} placement="left">
        <Link to={{ pathname: `/${resourcePath}/edit/${record._id}` }}>
          <EditOutlined />
        </Link>
      </Tooltip>
      {record?.is_archived ? (
        <Popconfirm
          title={t('datatable.column.action.unarchived.title')}
          okText={t('datatable.column.action.unarchived.ok')}
          okButtonProps={{ type: 'danger' }}
          cancelText={t('datatable.column.action.unarchived.cancel')}
          onConfirm={() => restoreResource(record?._id)}
          icon={<WarningOutlined />}
        >
          <Tooltip
            title={t('datatable.column.action.unarchived.hover')}
            placement="left"
          >
            <RollbackOutlined />
          </Tooltip>
        </Popconfirm>
      ) : (
        <Popconfirm
          title={t('datatable.column.action.archive.title')}
          okText={t('datatable.column.action.archive.ok')}
          okButtonProps={{ type: 'danger' }}
          cancelText={t('datatable.column.action.archive.cancel')}
          onConfirm={() => deleteResource(record?._id)}
          icon={<WarningOutlined />}
        >
          <Tooltip
            title={t('datatable.column.action.archive.hover')}
            placement="left"
          >
            <ContainerOutlined type="delete" />
          </Tooltip>
        </Popconfirm>
      )}
    </div>
  )
});
